import { Transition } from '@headlessui/react'
import { ErrorTransformer, RJSFSchema, UiSchema } from '@rjsf/utils'
import { cloneDeep, flattenDeep, isEmpty, kebabCase, keys, merge, omit, omitBy, orderBy, uniqBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useMutation } from 'react-query'

import Loading from '@/components/Loading'
import Button from '@/components/TailwindUI/Button'
import useMessage from '@/hooks/useMessage'
import { useNotification } from '@/hooks/useNotification'
import { WorkgridDatasourceForm } from '@/pages/SpaceAppBuilder/components/WorkgridDatasourceForm'
import convertJSONtoSchema from '@/pages/SpaceAppBuilder/utilities/convertJSONToSchema'
import { getAccessToken, getSpaceId } from '@/store/contextSubscriber'

import { extractUiSchema } from './components/Form'
import Monaco from './components/Monaco'
import proxyAction from './globalActions/proxy.json'
import useDatasource from './hooks/useDatasource'
import useSearchParams from './hooks/useSearchParams'
import extractUrlParams from './utilities/extractUrlParams'
import mergeObjectValues from './utilities/mergeObjectValues'

export function Test() {
  const [datasource, setDatasource] = useDatasource(true)
  const [isPendingUserAuthConnection, setIsPendingUserAuthConnection] = useState(false)
  const { notify } = useNotification()
  const events = cloneDeep(datasource.events)
  for (const key in datasource.events) {
    datasource.events[key].type = 'eventAction'
  }

  const datasourceActions: typeof datasource.actions = {
    _proxy: proxyAction,
    ...datasource.actions,
    ...events
  }

  const [searchParams] = useSearchParams()
  const activeActionKey = searchParams.get('action')!

  const callbackUrl = `${window.location.origin}/console/datasource/callback`

  useMessage('workgrid-auth', (send, payload) => {
    if (payload.success) {
      if (payload.code) {
        setFormData((formData: any) => ({
          ...formData,
          state: { code: payload.code, redirectUrl: callbackUrl },
          action: { ...formData.action, action: '_auth' }
        }))
      }
      notify({
        variant: 'success',
        title: 'Connection Successful',
        body: 'Grant Code has been stored in state. You can now call the _auth action to exchange a code for an access token.'
      })
      setIsPendingUserAuthConnection(false)
    } else {
      notify({
        variant: 'error',
        title: 'Connection Failed',
        body: 'Failed to retrieve Grant Code.'
      })
      setIsPendingUserAuthConnection(false)
    }
  })

  const mutation = useMutation({
    mutationFn: async (variables: {
      action: string
      configuration?: Record<string, unknown>
      inputs?: Record<string, unknown>
      state?: Record<string, unknown>
    }) => {
      let response
      if (process.env.NODE_ENV === 'production') {
        response = await fetch(`/v3/appbuilder/spaces/${getSpaceId()}/datasources/execute`, {
          method: 'post',
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: omit(datasourceActions[variables.action], 'response'),
            configuration: variables.configuration ?? {},
            inputs: variables.inputs ?? {},
            state: variables.state ?? {}
          })
        })
      } else {
        // modifing the url to account for when we are running the workgrid-datasource-api locally
        response = await fetch(`http://localhost:3001/api/v1/datasources/execute`, {
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
            // local dev headers to satisfy schema
            'x-workgrid-space': 'spaceId',
            'x-workgrid-tenant': 'tenantId',
            'x-workgrid-user': 'userId'
          },
          body: JSON.stringify({
            action: omit(datasourceActions[variables.action], 'response'),
            configuration: variables.configuration ?? {},
            inputs: variables.inputs ?? {},
            state: variables.state ?? {}
          })
        })
      }
      return response.json()
    },
    onSuccess: data => {
      if (data?.errors) return
      if (data?.results?.oauthUrl) {
        setIsPendingUserAuthConnection(true)
        const urlObj = new URL(data.results.oauthUrl)
        const params = new URLSearchParams(urlObj.search)

        // Checking if the 'state' parameter exists
        if (params.has('state')) {
          params.set('state', `${params.get('state')}&datasourceBuilder=true`)
        } else {
          params.append('state', 'datasourceBuilder=true')
        }

        // Updating the URL with the modified query parameters
        urlObj.search = params.toString()
        const modifiedUrl = urlObj.toString()

        const authWindow = window.open(modifiedUrl, '_blank')
        authWindow?.focus()
      }
      setFormData((formData: any) => ({
        ...formData,
        state: data.state
      }))
    }
  })

  const [formData, setFormData] = useState(() => {
    const value = localStorage.getItem(`datasource-${datasource.id}-instance`)
    const instanceData = value ? JSON.parse(value) : {}
    instanceData.action = {
      ...instanceData.action,
      action: activeActionKey
    }
    return instanceData
  })

  const schema = {
    type: 'object',
    properties: {
      configuration: {
        title: 'Configuration',
        ...datasource.schema
      },
      action: {
        type: 'object',
        title: 'Action',
        properties: {
          action: {
            type: 'string',
            title: 'Action',
            oneOf: orderBy(Object.keys(datasourceActions), value => value.toLowerCase()).map(actionKey => ({
              enum: [actionKey],
              title: `${datasourceActions[actionKey].name || actionKey} ${
                datasourceActions[actionKey].type === 'eventAction' ? ' (Event)' : ''
              } `
            }))
          }
        },
        required: ['action'],
        dependencies: {
          action: {
            oneOf: Object.entries(datasourceActions).map(([actionKey, action]) => ({
              properties: {
                action: {
                  enum: [actionKey]
                },
                ...(datasourceActions[actionKey].type === 'eventAction'
                  ? {
                      inputs: {
                        type: 'object',
                        title: 'Inputs',
                        properties: {
                          lastPollTime: {
                            type: 'string',
                            enum: ['5', '10', '15', '1440'],
                            title: 'Last Poll Time (minutes ago)'
                          }
                        },
                        required: []
                      }
                    }
                  : actionKey === '_getOauthUrl'
                    ? {
                        inputs: {
                          type: 'object',
                          title: 'Inputs',
                          properties: {
                            redirectUrl: {
                              type: 'string',
                              default: callbackUrl,
                              'x-ui:widget': 'hidden'
                            },
                            state: {
                              type: 'string',
                              default: 'datasourceBuilder=true',
                              'x-ui:widget': 'hidden'
                            }
                          },
                          required: []
                        }
                      }
                    : {
                        inputs: {
                          type: 'object',
                          title: 'Inputs',
                          ...action.inputs
                        }
                      })
              }
            }))
          }
        }
      },
      state: {
        type: 'object',
        title: 'State',
        default: {}
      }
    }
  } as RJSFSchema

  const uiSchema: UiSchema<any, typeof schema> = merge(extractUiSchema(schema), {
    'ui:options': {
      submitButtonOptions: {
        submitText: mutation.isLoading ? 'Executing...' : 'Execute',
        props: { disabled: mutation.isLoading, className: 'w-full justify-center' }
      }
    },
    configuration: {
      callbackUrl: {
        'ui:widget': 'CallBackUrl'
      }
    }
  })

  useEffect(() => {
    if (!formData) return

    const getHiddenFields = (schema: Record<string, any>, prefix = ''): string[] => {
      const collectHiddenFields = (schema: Record<string, any>, prefix: string, hiddenFields: string[]): void => {
        if (schema.properties) {
          for (const [key, value] of Object.entries(schema.properties) as [string, Record<string, any>][]) {
            if (value['x-ui:widget'] === 'hidden') {
              hiddenFields.push(`${key}`)
            }
          }
        }

        if (schema.dependencies) {
          for (const [key, value] of Object.entries(schema.dependencies) as [string, Record<string, any>][]) {
            if (value.oneOf) {
              value.oneOf.forEach((subSchema: Record<string, any>) => {
                collectHiddenFields(subSchema as Record<string, any>, `${prefix}${key}.`, hiddenFields)
              })
            }
          }
        }
      }

      const hiddenFields: string[] = []
      collectHiddenFields(schema, prefix, hiddenFields)

      return hiddenFields
    }

    const hiddenFields = getHiddenFields(datasource.schema)
    const modifiedFormData = { ...formData, configuration: omit(formData.configuration, hiddenFields) }
    localStorage.setItem(`datasource-${datasource.id}-instance`, JSON.stringify(modifiedFormData))
  }, [formData, datasource.id, datasource.schema])

  const updateResponseModelMutation = useMutation({
    mutationFn: async (variables: { actionKey: string; results: object; isEventAction?: boolean }) => {
      let schema = [] as any

      if (variables.isEventAction) {
        // @ts-expect-error
        schema = convertJSONtoSchema(variables.results?.[0])
        return setDatasource(datasource => ({
          ...datasource,
          events: {
            ...datasource.events,
            [variables.actionKey]: {
              ...datasource.events[variables.actionKey],
              eventModel: schema
            }
          }
        }))
      } else {
        schema = convertJSONtoSchema(variables.results)
        return setDatasource(datasource => ({
          ...datasource,
          actions: {
            ...datasource.actions,
            [variables.actionKey]: {
              ...datasource.actions[variables.actionKey],
              response: schema
            }
          }
        }))
      }
    }
  })

  const updateActionModelMutation = useMutation({
    mutationFn: async (variables: { actionKey: string; results: object }) => {
      const schema = convertJSONtoSchema(variables.results)

      function convertUrlToTitle(path: string): string {
        // Remove leading slash and split the path by "/"
        const pathSegments = path.replace(/^\//, '').split('/')

        // Replace placeholders and capitalize words
        const actionName = pathSegments
          .map(segment => {
            if (segment.includes('${params.')) {
              // Extract the parameter name from the segment
              const param = segment.match(/\${params\.([^}]*)}/)?.[1] || ''
              // Replace the placeholder with the parameter name
              return segment.replace(/\${params\.[^}]*}/, param)
            } else {
              return segment
            }
          })
          .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
          .join(' ')

        return actionName
      }

      const url = formData.action.inputs.path
      const method = formData.action.inputs.method
      const title = convertUrlToTitle(url)

      function mergeObjects(...objects: string[]): Record<string, any> {
        return merge({}, ...objects)
      }

      const mergedObjects = mergeObjects(
        formData.action.inputs.params ?? {},
        formData.action.inputs.query ?? {},
        formData.action.inputs.headers ?? {},
        formData.action.inputs.data ?? {}
      )
      const inputSchema = isEmpty(mergedObjects) ? undefined : convertJSONtoSchema(mergedObjects)

      function replaceValueWithKey(obj: string) {
        const result = {} as Record<string, any>
        for (const [key] of Object.entries(obj)) {
          result[key] = `inputs['${key}']`
        }
        return result
      }

      function removeSurroundingQuotesFromValues(obj: Record<string, any>) {
        const objectKeys = flattenDeep(keys(obj))

        const jsonString = JSON.stringify(obj).replaceAll(/"([^"]+?)"/g, (match, p1) => {
          if (objectKeys.includes(p1)) {
            return `"${p1}"`
          }
          return p1
        })

        return jsonString
      }

      // Remove quotes from all values in the stringified object
      const injectedPathParam = removeSurroundingQuotesFromValues(
        replaceValueWithKey(formData.action.inputs.params ?? {})
      )

      const injectedQueryParam = removeSurroundingQuotesFromValues(
        replaceValueWithKey(formData.action.inputs.query ?? {})
      )
      const injectedHeaders = removeSurroundingQuotesFromValues(
        replaceValueWithKey(formData.action.inputs.headers ?? {})
      )

      const injectedData = formData.action.inputs.data
        ? removeSurroundingQuotesFromValues(replaceValueWithKey(formData.action.inputs.data))
        : null

      // Generate the code snippet dynamically
      const code = `
const pathParams = ${injectedPathParam}
const method = '${formData.action.inputs.method}'
let path = '${formData.action.inputs.path}'


// Substitute path parameters
for (const key in pathParams) {
  path = path.replace(\`\\\${params.\${key}}\`, encodeURIComponent(pathParams[key]))
}

let url = new URL(path, inputs.baseUrl).toString()

function removeAttributesIfFalsy(obj) {
  for (const key in obj) {
    if (!obj[key]) {
      delete obj[key];
    }
  }
  return obj
}

if (Object.keys(removeAttributesIfFalsy(${injectedQueryParam})).length > 0) {
url = \`\${url}?\${new URLSearchParams(${injectedQueryParam})}\`
}
const response = await client.fetch(url, {
  method,
  headers: ${injectedHeaders},
  body: ${injectedData}
})

export default await response.json()
`

      return setDatasource(datasource => ({
        ...datasource,
        actions: {
          ...datasource.actions,
          [`${method}-${kebabCase(title)}`]: {
            name: title,
            ...datasource.actions[variables.actionKey],
            inputs: inputSchema,
            perform: code,
            response: schema
          }
        }
      }))
    }
  })

  useEffect(() => {
    if (updateResponseModelMutation.isSuccess) {
      const timeoutId = setTimeout(() => updateResponseModelMutation.reset(), 5000)
      return () => clearTimeout(timeoutId)
    }
  }, [updateResponseModelMutation.isSuccess, updateResponseModelMutation.reset])

  useEffect(() => {
    if (formData?.action?.action === '_proxy') {
      const formDataClone = cloneDeep(formData)

      if (formData.action.inputs?.path) {
        const params = extractUrlParams(formData.action.inputs.path)
        let currentActionParams = {}
        try {
          currentActionParams = {
            ...formDataClone.action?.inputs?.params,
            ...formDataClone.action?.inputs,
            ...formDataClone.configuration
          }
        } catch {}

        formDataClone.action.inputs.params = mergeObjectValues(params, currentActionParams)
      }
      setFormData(formDataClone)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.action?.inputs?.path])

  const transformErrors: ErrorTransformer = errors => {
    return uniqBy(
      errors.filter(error => error.name !== 'oneOf' && error.name !== 'enum'),
      errors => errors.property
    )
  }
  return (
    <div className="flex flex-1 divide-x">
      {isPendingUserAuthConnection ? (
        <div className="mr-auto ml-auto flex max-h-[500px] flex-col justify-center border-none">
          <Loading
            isLoading={isPendingUserAuthConnection}
            loadingMessage={`Redirecting to ${datasource.name || datasource.id}...`}
          />
          {isPendingUserAuthConnection && (
            <div className="ml-auto mr-auto -mt-40 max-w-[200px]">
              <Button
                color="secondary"
                type="button"
                onClick={() => {
                  setIsPendingUserAuthConnection(false)
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      ) : (
        <>
          <WorkgridDatasourceForm
            initialFormData={formData}
            schema={schema}
            transformErrors={transformErrors}
            uiSchema={uiSchema}
            onChange={updatedFormData => {
              const isEvent = datasourceActions[activeActionKey]?.type === 'eventAction'
              localStorage.setItem(
                `${datasource.id}-active-${isEvent ? 'event-' : ''}action`,
                updatedFormData.action.action
              )
              if (updatedFormData.action.action !== formData.action.action) {
                updatedFormData.action.inputs = {}
              }
              setFormData(updatedFormData)
            }}
            className="max-h-[73vh] w-96"
            onSubmit={data => {
              mutation.mutate({
                action: data.formData.action.action,
                configuration: data.formData.configuration,
                inputs: omitBy(data.formData.action.inputs, v => '{}' === v),
                state: data.formData.state
              })
            }}
          />

          <div className="flex flex-1 flex-col">
            {mutation.data ? (
              <>
                <div className="flex flex-1 flex-grow-[2] flex-col">
                  <div className="bg-gray-100 px-4 py-2 text-xs">Results</div>
                  <div className="relative flex-1">
                    <div className="absolute right-20 top-0.5 z-10 flex flex-col items-center">
                      <div className="flex">
                        {formData && formData?.action?.action === '_proxy' && (
                          <>
                            <Button
                              className="mr-2 flex"
                              color="secondary"
                              onClick={() => {
                                if (mutation.variables) {
                                  updateActionModelMutation.mutate({
                                    actionKey: mutation.variables.action,
                                    results: mutation.data.results ?? mutation.data
                                  })
                                }
                              }}
                            >
                              Generate Action
                            </Button>
                            <Transition
                              show={
                                updateActionModelMutation.status === 'success' ||
                                updateActionModelMutation.status === 'error'
                              }
                              enter="transition-opacity duration-125"
                              enterFrom="opacity-0"
                              enterTo="opacity-100"
                              leave="transition-opacity duration-300"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              as="div"
                              className="absolute top-9 right-4 z-10 flex select-none text-sm font-thin text-gray-400"
                            >
                              {updateActionModelMutation.status === 'success'
                                ? 'Success! View in Actions'
                                : 'Error creating action'}
                            </Transition>
                          </>
                        )}
                        <Transition
                          show={updateResponseModelMutation.isSuccess || updateResponseModelMutation.isError}
                          enter="transition-opacity duration-125"
                          enterFrom="opacity-0"
                          enterTo="opacity-100"
                          leave="transition-opacity duration-300"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          as="div"
                          className="pointer-events-none pr-2 text-sm font-thin text-gray-400"
                        >
                          {updateResponseModelMutation.isSuccess
                            ? 'Success! View in Import/Export'
                            : updateResponseModelMutation.isError
                              ? 'Error updating schema'
                              : ''}
                        </Transition>
                        <Button
                          color="secondary"
                          onClick={() => {
                            if (mutation.variables) {
                              updateResponseModelMutation.mutate({
                                isEventAction: datasourceActions[formData?.action.action].type === 'eventAction',
                                actionKey: mutation.variables.action,
                                results: mutation.data.results ?? mutation.data
                              })
                            }
                          }}
                        >
                          {datasourceActions[formData?.action.action].type === 'eventAction'
                            ? 'Generate Event Model Schema'
                            : 'Generate Response Schema'}
                        </Button>
                      </div>
                    </div>
                    <Monaco
                      className="pt-10"
                      language="json"
                      value={JSON.stringify(mutation.data.results ?? mutation.data, null, 2)}
                      options={{ readOnly: true }}
                    />
                  </div>
                </div>
                <div className="flex flex-1">
                  <div className="flex flex-1 flex-col">
                    <div className="bg-gray-100 px-4 py-2 text-xs">Logs</div>
                    <Monaco language="log" value={mutation.data.logs} options={{ readOnly: true }} />
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="bg-gray-100 px-4 py-2 text-xs">State</div>
                    <div className="relative flex-1">
                      <div className="absolute right-6 top-0.5 z-10 flex flex-col items-center">
                        <div className="flex">
                          <Button
                            color="secondary"
                            onClick={() => {
                              setFormData((formData: any) => ({ ...formData, state: mutation.data.state }))
                            }}
                          >
                            Use State
                          </Button>
                        </div>
                      </div>
                      <Monaco
                        className="pt-10"
                        showCopyToClipboardButton={false}
                        language="json"
                        value={JSON.stringify(mutation.data.state, null, 2)}
                        options={{ readOnly: true }}
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}
